<template>
  <div class="export-button-container">
    <button
      class="export-button"
      :disabled="items.length === 0"
      @click="showModal = true"
      title="导出"
    >
      <span class="button-text">导出</span>
    </button>

    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h2 class="modal-title">选择导出格式</h2>
              <button class="modal-close" @click="showModal = false">×</button>
            </div>
            <div class="modal-body">
              <button class="export-option" @click="exportItems('markdown')">
                <span class="option-icon">📝</span>
                <div class="option-content">
                  <div class="option-title">Markdown</div>
                  <div class="option-desc">适合文档和笔记</div>
                </div>
              </button>
              <button class="export-option" @click="exportItems('json')">
                <span class="option-icon">🔧</span>
                <div class="option-content">
                  <div class="option-title">JSON</div>
                  <div class="option-desc">适合数据处理</div>
                </div>
              </button>
              <button class="export-option" @click="exportItems('csv')">
                <span class="option-icon">📊</span>
                <div class="option-content">
                  <div class="option-title">CSV</div>
                  <div class="option-desc">适合 Excel 表格</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useExport } from '../composables/useExport'
import type { Item } from '../api'
import { useToastStore } from '../stores/toast'

const props = defineProps<{
  items: Item[]
}>()

const toast = useToastStore()
const { exportItems: exportData } = useExport()

const showModal = ref(false)
const eventItems = ref<Item[] | null>(null)

function exportItems(format: 'markdown' | 'json' | 'csv') {
  try {
    const itemsToExport = eventItems.value || props.items
    exportData(itemsToExport, format)
    toast.success(`已导出为 ${format.toUpperCase()} 格式`)
    showModal.value = false
    eventItems.value = null
  } catch {
    toast.error('导出失败，请重试')
  }
}

// 监听自定义事件（用于移动端菜单触发）
onMounted(() => {
  window.addEventListener('open-export', ((e: CustomEvent<{ items: Item[] }>) => {
    eventItems.value = e.detail.items
    showModal.value = true
  }) as EventListener)
})
</script>

<style scoped>
.export-button-container {
  position: relative;
}

.export-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0;
  width: 40px;
  height: 40px;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border-radius: 50%;
  font-size: 0;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px var(--shadow-color);
  position: relative;
}

.export-button > * {
  visibility: hidden;
}

.export-button::before {
  content: '';
  width: 18px;
  height: 18px;
  background: currentColor;
  -webkit-mask: url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 18 18' fill='currentColor' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5 3a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V4a1 1 0 00-1-1H5zM3 4a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V4zm4 2a.5.5 0 000 1h4a.5.5 0 000-1H7zm0 3a.5.5 0 000 1h4a.5.5 0 000-1H7zm0 3a.5.5 0 000 1h4a.5.5 0 000-1H7z'/%3E%3C/svg%3E")
    no-repeat center;
  mask: url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 18 18' fill='currentColor' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5 3a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V4a1 1 0 00-1-1H5zM3 4a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V4zm4 2a.5.5 0 000 1h4a.5.5 0 000-1H7zm0 3a.5.5 0 000 1h4a.5.5 0 000-1H7zm0 3a.5.5 0 000 1h4a.5.5 0 000-1H7z'/%3E%3C/svg%3E")
    no-repeat center;
}

.export-button:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
  color: var(--color-primary);
  transform: scale(1.1);
  box-shadow: 0 4px 12px var(--shadow-color);
}

.export-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 暗夜模式 */
:global(.dark) .export-button {
  background: var(--bg-secondary);
  border-color: var(--border-color);
}

:global(.dark) .export-button:hover:not(:disabled) {
  border-color: var(--color-primary-light);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

/* 中心弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 480px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 8px;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: var(--border-color);
  color: var(--text-primary);
}

.modal-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.export-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid var(--border-color);
  background: var(--bg-secondary);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  color: var(--text-primary);
}

.export-option:hover {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-color);
}

.option-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.option-content {
  flex: 1;
}

.option-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.option-desc {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

/* 弹窗动画 */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}

/* 移动端优化 */
@media (max-width: 640px) {
  .modal-content {
    max-width: 100%;
    border-radius: 16px 16px 0 0;
    margin-top: auto;
  }

  .modal-overlay {
    align-items: flex-end;
    padding: 0;
  }

  .export-option {
    padding: 0.875rem;
  }

  .option-icon {
    font-size: 1.5rem;
  }

  .option-title {
    font-size: 0.875rem;
  }

  .option-desc {
    font-size: 0.75rem;
  }
}
</style>
